/**
 * Pipecraft Schema
 * Pipeline Crafter feature tables - CI/CD pipeline management
 */

import { pgTable, uuid, varchar, text, timestamp, boolean } from 'drizzle-orm/pg-core';
import { relations } from 'drizzle-orm';
import { profiles } from './profiles';

// =============================================================================
// Pipecraft Templates
// =============================================================================
export const pipecraftTemplates = pgTable('pipecraft_templates', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: varchar('name', { length: 255 }).notNull(),
  description: text('description').notNull(),
  templateType: varchar('template_type', { length: 100 }).notNull(),
  configuration: text('configuration').default('{}'), // JSON
  isActive: boolean('is_active').default(true),
  createdBy: uuid('created_by').notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// Pipecraft Integrations
// =============================================================================
export const pipecraftIntegrations = pgTable('pipecraft_integrations', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: varchar('name', { length: 255 }).notNull(),
  integrationType: varchar('integration_type', { length: 100 }).notNull(),
  configSchema: text('config_schema').default('{}'), // JSON
  iconUrl: text('icon_url'),
  isActive: boolean('is_active').default(true),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// Pipecraft Repositories
// =============================================================================
export const pipecraftRepositories = pgTable('pipecraft_repositories', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: varchar('name', { length: 255 }).notNull(),
  description: text('description'),
  repoUrl: text('repo_url'),
  templateId: uuid('template_id'),
  triggerTag: varchar('trigger_tag', { length: 255 }).notNull(),
  isPrivate: boolean('is_private').default(false),
  createdBy: uuid('created_by').notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// Pipecraft Profiles
// =============================================================================
export const pipecraftProfiles = pgTable('pipecraft_profiles', {
  id: uuid('id').primaryKey(), // References profiles.id
  displayName: varchar('display_name', { length: 255 }),
  githubUsername: varchar('github_username', { length: 255 }),
  preferredTemplate: uuid('preferred_template'),
  settings: text('settings'), // JSON
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// Pipecraft Pipeline Logs
// =============================================================================
export const pipecraftPipelineLogs = pgTable('pipecraft_pipeline_logs', {
  id: uuid('id').primaryKey().defaultRandom(),
  repositoryId: uuid('repository_id').notNull(),
  status: varchar('status', { length: 50 }).notNull(),
  logOutput: text('log_output'),
  startedAt: timestamp('started_at').defaultNow().notNull(),
  completedAt: timestamp('completed_at'),
  createdBy: uuid('created_by').notNull(),
});

// =============================================================================
// Pipecraft Repo Integration Map
// =============================================================================
export const pipecraftRepoIntegrationMap = pgTable('pipecraft_repo_integration_map', {
  id: uuid('id').primaryKey().defaultRandom(),
  repositoryId: uuid('repository_id').notNull(),
  integrationId: uuid('integration_id').notNull(),
  config: text('config').default('{}'), // JSON
  status: varchar('status', { length: 50 }),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// Pipecraft User Credentials
// =============================================================================
export const pipecraftUserCredentials = pgTable('pipecraft_user_credentials', {
  id: uuid('id').primaryKey().defaultRandom(),
  userId: uuid('user_id').notNull(),
  integrationId: uuid('integration_id').notNull(),
  credentials: text('credentials').notNull(), // JSON (encrypted)
  isValid: boolean('is_valid').default(true),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// Relations
// =============================================================================
export const pipecraftTemplatesRelations = relations(pipecraftTemplates, ({ one }) => ({
  createdByUser: one(profiles, {
    fields: [pipecraftTemplates.createdBy],
    references: [profiles.id],
  }),
}));

export const pipecraftRepositoriesRelations = relations(pipecraftRepositories, ({ one, many }) => ({
  template: one(pipecraftTemplates, {
    fields: [pipecraftRepositories.templateId],
    references: [pipecraftTemplates.id],
  }),
  createdByUser: one(profiles, {
    fields: [pipecraftRepositories.createdBy],
    references: [profiles.id],
  }),
  logs: many(pipecraftPipelineLogs),
  integrations: many(pipecraftRepoIntegrationMap),
}));

export const pipecraftPipelineLogsRelations = relations(pipecraftPipelineLogs, ({ one }) => ({
  repository: one(pipecraftRepositories, {
    fields: [pipecraftPipelineLogs.repositoryId],
    references: [pipecraftRepositories.id],
  }),
}));

export const pipecraftRepoIntegrationMapRelations = relations(
  pipecraftRepoIntegrationMap,
  ({ one }) => ({
    repository: one(pipecraftRepositories, {
      fields: [pipecraftRepoIntegrationMap.repositoryId],
      references: [pipecraftRepositories.id],
    }),
    integration: one(pipecraftIntegrations, {
      fields: [pipecraftRepoIntegrationMap.integrationId],
      references: [pipecraftIntegrations.id],
    }),
  })
);

export const pipecraftUserCredentialsRelations = relations(pipecraftUserCredentials, ({ one }) => ({
  integration: one(pipecraftIntegrations, {
    fields: [pipecraftUserCredentials.integrationId],
    references: [pipecraftIntegrations.id],
  }),
}));

// =============================================================================
// Types
// =============================================================================
export type PipecraftTemplate = typeof pipecraftTemplates.$inferSelect;
export type NewPipecraftTemplate = typeof pipecraftTemplates.$inferInsert;
export type PipecraftIntegration = typeof pipecraftIntegrations.$inferSelect;
export type NewPipecraftIntegration = typeof pipecraftIntegrations.$inferInsert;
export type PipecraftRepository = typeof pipecraftRepositories.$inferSelect;
export type NewPipecraftRepository = typeof pipecraftRepositories.$inferInsert;
export type PipecraftProfile = typeof pipecraftProfiles.$inferSelect;
export type NewPipecraftProfile = typeof pipecraftProfiles.$inferInsert;
export type PipecraftPipelineLog = typeof pipecraftPipelineLogs.$inferSelect;
export type NewPipecraftPipelineLog = typeof pipecraftPipelineLogs.$inferInsert;
export type PipecraftRepoIntegration = typeof pipecraftRepoIntegrationMap.$inferSelect;
export type NewPipecraftRepoIntegration = typeof pipecraftRepoIntegrationMap.$inferInsert;
export type PipecraftUserCredential = typeof pipecraftUserCredentials.$inferSelect;
export type NewPipecraftUserCredential = typeof pipecraftUserCredentials.$inferInsert;
